import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjects } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { API_URL, apiRequest } from '../config/api';

function EmissionActivity() {
  const { t } = useTranslation();
  const { selectedProject } = useProjects();
  const { user, handleAuthError } = useAuth();

  const projectId = selectedProject?.id;

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [emissionActivities, setEmissionActivities] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [newActivity, setNewActivity] = useState({
    activityId: '',
    name: '',
    category: 'TRANSPORT',
    subcategory: 'ROAD',
    source: 'GLEC_V31',
    region: 'GLOBAL',
    unit: 'KM',
    emissionFactor: 0,
    emissionFactorUnit: 'KM',
    fuelType: 'DIESEL',
    description: ''
  });

  // Fetch emission activities on mount
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchActivities = async () => {
      if (!user?.token) return;
      try {
        if (isMounted) setLoading(true);
        const response = await apiRequest(`${API_URL}/api/activities`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal
        }, handleAuthError);

        if (!response.ok) {
          throw new Error('Failed to fetch emission activities');
        }

        const data = await response.json();
        const items = Array.isArray(data) ? data : (Array.isArray(data?.content) ? data.content : []);
        if (isMounted) {
          setEmissionActivities(items);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Fetch emission activities error:', err);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchActivities();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [user?.token, handleAuthError]);

  const totalPages = Math.ceil((emissionActivities?.length || 0) / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = (emissionActivities || []).slice(startIndex, endIndex);

  

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (val) => {
    setItemsPerPage(val);
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewActivity(prev => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setShowModal(false);
    setNewActivity({
      activityId: '',
      name: '',
      category: 'TRANSPORT',
      subcategory: 'ROAD',
      source: 'GLEC_V31',
      region: 'GLOBAL',
      unit: 'KM',
      emissionFactor: 0,
      emissionFactorUnit: 'KM',
      fuelType: 'DIESEL',
      description: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newActivity.activityId || !newActivity.name) return;

    setIsSubmitting(true);
    try {
      const payload = {
        activityId: newActivity.activityId,
        name: newActivity.name,
        category: newActivity.category,
        subcategory: newActivity.subcategory,
        source: newActivity.source,
        region: newActivity.region,
        unit: newActivity.unit,
        emissionFactor: Number(newActivity.emissionFactor ?? 0),
        description: newActivity.description,
        fuelType: newActivity.fuelType,
        technology: '',
        emissionFactorUnit: newActivity.emissionFactorUnit
      };

      const response = await fetch(`${API_URL}/api/activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        // Optionally read error message
        // const errorData = await response.json();
        throw new Error('Failed to create emission activity');
      }

      // Optionally use response json
      // const created = await response.json();

      setEmissionActivities(prev => [
        { ...newActivity, emissionFactor: Number(newActivity.emissionFactor ?? 0) },
        ...prev
      ]);

      handleCancel();
    } catch (err) {
      console.error('Create emission activity error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!projectId) {
    return (
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-12">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('emissionActivity.noProjectSelected.title')}</h3>
          <p className="text-gray-500 mb-6">{t('emissionActivity.noProjectSelected.description')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('navigation.emissionActivity')}</h1>
        <div className="flex items-start justify-between">
          <p className="text-gray-600 text-sm leading-relaxed max-w-2xl">
            {t('emissionActivity.description')}
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="ml-6 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex-shrink-0"
          >
            {t('emissionActivity.modal.addActivity')}
          </button>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">{t('emissionActivity.table.activityId')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">{t('emissionActivity.table.name')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">{t('emissionActivity.table.category')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">{t('emissionActivity.table.subcategory')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">{t('emissionActivity.table.source')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">{t('emissionActivity.table.region')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">{t('emissionActivity.table.unit')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">{t('emissionActivity.table.efUnit')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">{t('emissionActivity.table.fuelType')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">{t('emissionActivity.table.description')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <>
                  {Array.from({ length: itemsPerPage }).map((_, idx) => (
                    <tr key={idx} className="animate-pulse">
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-28" /></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-40" /></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-28" /></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-32" /></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-48" /></td>
                    </tr>
                  ))}
                </>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <div className="text-gray-400 mb-4">
                      <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('emissionActivity.emptyState.title')}</h3>
                    <button
                      onClick={() => setShowModal(true)}
                      className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                    >
                      {t('emissionActivity.modal.addActivity')}
                    </button>
                  </td>
                </tr>
              ) : (
                currentItems.map((a) => (
                  <tr key={a.activityId} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a.activityId}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{a.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{a.category}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{a.subcategory}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{a.source}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{a.region}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{a.unit}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{a.emissionFactorUnit}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{a.fuelType}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{a.description}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && (
        <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="text-sm text-gray-700">
              {t('emissionActivity.pagination.showing', { start: startIndex + 1, end: Math.min(endIndex, emissionActivities?.length || 0), total: emissionActivities?.length || 0 })}
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">{t('emissionActivity.pagination.show')}</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="text-sm bg-gray-50 border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-700">{t('emissionActivity.pagination.perPage')}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded">
              {currentPage} {t('emissionActivity.pagination.of')} {totalPages}
            </span>
            <button
              className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('emissionActivity.modal.addTitle')}</h3>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionActivity.table.activityId')}</label>
                    <input
                      type="text"
                      name="activityId"
                      value={newActivity.activityId}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionActivity.table.name')}</label>
                    <input
                      type="text"
                      name="name"
                      value={newActivity.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionActivity.table.emissionFactor')}</label>
                    <input
                      type="number"
                      step="0.000001"
                      name="emissionFactor"
                      value={newActivity.emissionFactor}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionActivity.table.category')}</label>
                    <input
                      type="text"
                      name="category"
                      value={newActivity.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionActivity.table.subcategory')}</label>
                    <input
                      type="text"
                      name="subcategory"
                      value={newActivity.subcategory}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionActivity.table.source')}</label>
                    <input
                      type="text"
                      name="source"
                      value={newActivity.source}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionActivity.table.region')}</label>
                    <input
                      type="text"
                      name="region"
                      value={newActivity.region}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionActivity.table.unit')}</label>
                    <input
                      type="text"
                      name="unit"
                      value={newActivity.unit}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionActivity.table.efUnit')}</label>
                    <input
                      type="text"
                      name="emissionFactorUnit"
                      value={newActivity.emissionFactorUnit}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionActivity.table.fuelType')}</label>
                    <input
                      type="text"
                      name="fuelType"
                      value={newActivity.fuelType}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionActivity.table.description')}</label>
                    <textarea
                      name="description"
                      value={newActivity.description}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="3"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors duration-200"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? t('emissionActivity.modal.saving') : t('emissionActivity.modal.addActivity')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmissionActivity;


